import { CompanyInfo } from "@/types/types";
import React from "react";
import FacebookIcon from "./icons/FacebookIcon";
import InstagramIcon from "./icons/InstagramIcon";
import YoutubeIcon from "./icons/YoutubeIcon";
import TwitterIcon from "./icons/TwitterIcon";
import PinterestIcon from "./icons/PinterestIcon";
import TripAdvisorIcon from "./icons/TripAdvisorIcon";
import YelpIcon from "./icons/YelpIcon";
import { getDisplayAddress, getMapUrl } from "@/services/menuService";

interface MenuInfoSectionProps {
  menuTitle: string;
  tagLine: string;
  companyInfo: CompanyInfo;
}

const MenuInfoSection: React.FC<MenuInfoSectionProps> = ({
  menuTitle,
  tagLine,
  companyInfo,
}) => {
  const simpliCoffeeAddresses = [
    {
      name: "Av. Fontes Pereira de Melo 41 C, 119 , Lisboa, Portugal",
      link: "https://maps.app.goo.gl/D5Z4jNkozYBtcMe29",
    },
    {
      name: "R. Braamcamp 64, 1250-096 Lisboa, Portugal",
      link: "https://maps.app.goo.gl/2oGc4aPYxuDn7ztu8",
    },
    {
      name: "Largo da Anunciada 20, 1150-267 Lisboa",
      link: "https://maps.app.goo.gl/VvevkodXDvP3RmUR9",
    },
    {
      name: "Praça Dom João I 39, 4000-259 Porto, Portugal",
      link: "https://maps.app.goo.gl/Z6Lhph1MLRkzNygd6",
    },
  ];

  const ensureHttps = (url: string | undefined): string => {
    if (!url) {
      return ""; // or handle undefined URLs as needed
    }

    if (url.startsWith("http://")) {
      return url.replace("http://", "https://");
    } else if (!url.startsWith("https://")) {
      return "https://" + url;
    }
    return url;
  };

  const normalizeURL = (url: string, base: string) => {
    if (!url) return url;
    if (url.startsWith("http://") || url.startsWith("https://")) {
      return url;
    } else if (url.includes(".com") || url.includes(".net")) {
      return `https://${url}`;
    } else {
      return `https://${base}/${url}`;
    }
  };

  const SocialMediaLinks = ({ companyInfo }: any) => (
    <div className="social-links flex items-center gap-3 mt-3">
      {companyInfo?.instagramLink && (
        <a
          href={normalizeURL(companyInfo.instagramLink, "instagram.com")}
          target="_blank"
          rel="noopener noreferrer"
        >
          <InstagramIcon />
        </a>
      )}
      {companyInfo?.facebookLink && (
        <a
          href={normalizeURL(companyInfo.facebookLink, "facebook.com")}
          target="_blank"
          rel="noopener noreferrer"
        >
          <FacebookIcon />
        </a>
      )}
      {companyInfo?.youtubeLink && (
        <a
          href={normalizeURL(companyInfo.youtubeLink, "youtube.com")}
          target="_blank"
          rel="noopener noreferrer"
        >
          <YoutubeIcon />
        </a>
      )}
      {companyInfo?.twitterLink && (
        <a
          href={normalizeURL(companyInfo.twitterLink, "twitter.com")}
          target="_blank"
          rel="noopener noreferrer"
        >
          <TwitterIcon />
        </a>
      )}
      {companyInfo?.pinterestLink && (
        <a
          href={normalizeURL(companyInfo.pinterestLink, "pinterest.com")}
          target="_blank"
          rel="noopener noreferrer"
        >
          <PinterestIcon />
        </a>
      )}
      {companyInfo?.tripAdvisorLink && (
        <a
          href={normalizeURL(companyInfo.tripAdvisorLink, "tripadvisor.com")}
          target="_blank"
          rel="noopener noreferrer"
        >
          <TripAdvisorIcon />
        </a>
      )}
      {companyInfo?.yelpLink && (
        <a
          href={normalizeURL(companyInfo.yelpLink, "yelp.com")}
          target="_blank"
          rel="noopener noreferrer"
        >
          <YelpIcon />
        </a>
      )}
    </div>
  );

  return (
    <div className="m-[5%] py-2 bg-white">
      <h2
        className={`text-[22px] font-bold text-gray-900 leading-[30px] ${
          menuTitle.includes("II") ? "" : "capitalize"
        }`}
      >
        {menuTitle}
      </h2>
      <span className="text-[15px] text-gray-900 leading-[10px]">
        {tagLine}
      </span>
      <div className="mt-3">
        {companyInfo?.website && (
          <a
            href={ensureHttps(companyInfo?.website)}
            className="text-[14px] text-black-600 hover:text-blue-800 transition duration-300 flex items-center gap-2"
            target="_blank"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="25"
              height="25"
              viewBox="-2 0 22 22"
              fill="none"
              style={{ minWidth: "25px" }}
            >
              <g clipPath="url(#clip0_169_699)">
                <path
                  d="M10 0C10.918 0 11.8034 0.117188 12.6562 0.351562C13.5091 0.585938 14.3066 0.921224 15.0488 1.35742C15.791 1.79362 16.4648 2.31445 17.0703 2.91992C17.6758 3.52539 18.1966 4.20247 18.6328 4.95117C19.069 5.69987 19.4043 6.4974 19.6387 7.34375C19.873 8.1901 19.9935 9.07552 20 10C20 10.918 19.8828 11.8034 19.6484 12.6562C19.4141 13.5091 19.0788 14.3066 18.6426 15.0488C18.2064 15.791 17.6855 16.4648 17.0801 17.0703C16.4746 17.6758 15.7975 18.1966 15.0488 18.6328C14.3001 19.069 13.5026 19.4043 12.6562 19.6387C11.8099 19.873 10.9245 19.9935 10 20C9.08203 20 8.19661 19.8828 7.34375 19.6484C6.49089 19.4141 5.69336 19.0788 4.95117 18.6426C4.20898 18.2064 3.53516 17.6855 2.92969 17.0801C2.32422 16.4746 1.80339 15.7975 1.36719 15.0488C0.93099 14.3001 0.595703 13.5059 0.361328 12.666C0.126953 11.8262 0.00651042 10.9375 0 10C0 9.08203 0.117188 8.19661 0.351562 7.34375C0.585938 6.49089 0.921224 5.69336 1.35742 4.95117C1.79362 4.20898 2.31445 3.53516 2.91992 2.92969C3.52539 2.32422 4.20247 1.80339 4.95117 1.36719C5.69987 0.93099 6.49414 0.595703 7.33398 0.361328C8.17383 0.126953 9.0625 0.00651042 10 0ZM10 18.75C10.8008 18.75 11.5723 18.6458 12.3145 18.4375C13.0566 18.2292 13.7533 17.9362 14.4043 17.5586C15.0553 17.181 15.6478 16.722 16.1816 16.1816C16.7155 15.6413 17.1712 15.0521 17.5488 14.4141C17.9264 13.776 18.2227 13.0794 18.4375 12.3242C18.6523 11.569 18.7565 10.7943 18.75 10C18.75 9.19922 18.6458 8.42773 18.4375 7.68555C18.2292 6.94336 17.9362 6.24674 17.5586 5.5957C17.181 4.94466 16.722 4.35221 16.1816 3.81836C15.6413 3.28451 15.0521 2.82878 14.4141 2.45117C13.776 2.07357 13.0794 1.77734 12.3242 1.5625C11.569 1.34766 10.7943 1.24349 10 1.25C9.19922 1.25 8.42773 1.35417 7.68555 1.5625C6.94336 1.77083 6.24674 2.0638 5.5957 2.44141C4.94466 2.81901 4.35221 3.27799 3.81836 3.81836C3.28451 4.35872 2.82878 4.94792 2.45117 5.58594C2.07357 6.22396 1.77734 6.92057 1.5625 7.67578C1.34766 8.43099 1.24349 9.20573 1.25 10C1.25 10.8008 1.35417 11.5723 1.5625 12.3145C1.77083 13.0566 2.0638 13.7533 2.44141 14.4043C2.81901 15.0553 3.27799 15.6478 3.81836 16.1816C4.35872 16.7155 4.94792 17.1712 5.58594 17.5488C6.22396 17.9264 6.92057 18.2227 7.67578 18.4375C8.43099 18.6523 9.20573 18.7565 10 18.75ZM15.8301 10.1562L16.2988 8.75H17.0312L16.2012 11.25H15.4688L15 9.84375L14.5312 11.25H13.7988L12.9688 8.75H13.7012L14.1699 10.1562L14.6387 8.75H15.3613L15.8301 10.1562ZM11.2988 8.75H12.0312L11.2012 11.25H10.4688L10 9.84375L9.53125 11.25H8.79883L7.96875 8.75H8.70117L9.16992 10.1562L9.63867 8.75H10.3613L10.8301 10.1562L11.2988 8.75ZM6.29883 8.75H7.03125L6.20117 11.25H5.46875L5 9.84375L4.53125 11.25H3.79883L2.96875 8.75H3.70117L4.16992 10.1562L4.63867 8.75H5.36133L5.83008 10.1562L6.29883 8.75Z"
                  fill="black"
                />
              </g>
              <defs>
                <clipPath id="clip0_169_699">
                  <rect width="20" height="20" fill="white" />
                </clipPath>
              </defs>
            </svg>
            {companyInfo?.website}
          </a>
        )}

        {/* special request for a client from Patrick */}
        {menuTitle === "Simpli coffee" ? (
          simpliCoffeeAddresses.map((address) => (
            <a
              key={address.link}
              href={address.link}
              className="text-[14px] text-black-600 hover:text-blue-800 transition duration-300 flex items-center justify-start gap-2 my-2"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="25"
                height="25"
                viewBox="1 2 20 20"
                fill="none"
                style={{ minWidth: "25px" }}
              >
                <path
                  d="M12.4997 14.5833C14.7976 14.5833 16.6663 12.7146 16.6663 10.4167C16.6663 8.11875 14.7976 6.25 12.4997 6.25C10.2018 6.25 8.33301 8.11875 8.33301 10.4167C8.33301 12.7146 10.2018 14.5833 12.4997 14.5833ZM12.4997 8.33333C13.6486 8.33333 14.583 9.26771 14.583 10.4167C14.583 11.5656 13.6486 12.5 12.4997 12.5C11.3507 12.5 10.4163 11.5656 10.4163 10.4167C10.4163 9.26771 11.3507 8.33333 12.4997 8.33333Z"
                  fill="black"
                />
                <path
                  d="M11.8963 22.7228C12.0725 22.8487 12.2838 22.9164 12.5004 22.9164C12.7171 22.9164 12.9283 22.8487 13.1046 22.7228C13.4212 22.4989 20.864 17.1249 20.8337 10.4166C20.8337 5.82179 17.0952 2.08325 12.5004 2.08325C7.90563 2.08325 4.16708 5.82179 4.16708 10.4114C4.13688 17.1249 11.5796 22.4989 11.8963 22.7228ZM12.5004 4.16659C15.9473 4.16659 18.7504 6.96971 18.7504 10.4218C18.7723 15.0447 14.1796 19.1958 12.5004 20.5572C10.8223 19.1947 6.22854 15.0426 6.25042 10.4166C6.25042 6.96971 9.05354 4.16659 12.5004 4.16659Z"
                  fill="black"
                />
              </svg>
              {address.name}
            </a>
          ))
        ) : (
          <a
            href={getMapUrl(companyInfo)}
            className="text-[14px] text-black-600 hover:text-blue-800 transition duration-300 flex items-center justify-start gap-2 my-2"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="25"
              height="25"
              viewBox="1 2 20 20"
              fill="none"
              style={{ minWidth: "25px" }}
            >
              <path
                d="M12.4997 14.5833C14.7976 14.5833 16.6663 12.7146 16.6663 10.4167C16.6663 8.11875 14.7976 6.25 12.4997 6.25C10.2018 6.25 8.33301 8.11875 8.33301 10.4167C8.33301 12.7146 10.2018 14.5833 12.4997 14.5833ZM12.4997 8.33333C13.6486 8.33333 14.583 9.26771 14.583 10.4167C14.583 11.5656 13.6486 12.5 12.4997 12.5C11.3507 12.5 10.4163 11.5656 10.4163 10.4167C10.4163 9.26771 11.3507 8.33333 12.4997 8.33333Z"
                fill="black"
              />
              <path
                d="M11.8963 22.7228C12.0725 22.8487 12.2838 22.9164 12.5004 22.9164C12.7171 22.9164 12.9283 22.8487 13.1046 22.7228C13.4212 22.4989 20.864 17.1249 20.8337 10.4166C20.8337 5.82179 17.0952 2.08325 12.5004 2.08325C7.90563 2.08325 4.16708 5.82179 4.16708 10.4114C4.13688 17.1249 11.5796 22.4989 11.8963 22.7228ZM12.5004 4.16659C15.9473 4.16659 18.7504 6.96971 18.7504 10.4218C18.7723 15.0447 14.1796 19.1958 12.5004 20.5572C10.8223 19.1947 6.22854 15.0426 6.25042 10.4166C6.25042 6.96971 9.05354 4.16659 12.5004 4.16659Z"
                fill="black"
              />
            </svg>
            {getDisplayAddress(companyInfo)}
          </a>
        )}

        {companyInfo?.phoneNumber && (
          <a
            href={`tel:${companyInfo?.phoneNumber}`}
            className="text-[14px] text-black-600 hover:text-blue-800 transition duration-300 flex items-center gap-2"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="25"
              height="25"
              viewBox="0 0 22 22"
              fill="none"
              style={{ minWidth: "25px" }}
            >
              <path
                d="M19.9272 15.046C19.774 16.2105 19.2021 17.2794 18.3184 18.053C17.4346 18.8267 16.2995 19.2522 15.125 19.2501C8.30157 19.2501 2.75001 13.6985 2.75001 6.87507C2.7479 5.70055 3.17341 4.56543 3.94706 3.68171C4.72071 2.798 5.78959 2.22612 6.95407 2.07288C7.24854 2.03693 7.54674 2.09717 7.80415 2.24462C8.06157 2.39207 8.26439 2.61881 8.38235 2.89101L10.1974 6.94296V6.95327C10.2877 7.16163 10.325 7.38911 10.3059 7.6154C10.2869 7.84169 10.2121 8.05974 10.0882 8.25007C10.0727 8.27327 10.0564 8.29476 10.0392 8.31624L8.25001 10.4372C8.89368 11.7451 10.2618 13.1012 11.587 13.7466L13.6787 11.9669C13.6992 11.9496 13.7207 11.9335 13.7431 11.9187C13.9333 11.7919 14.1521 11.7145 14.3797 11.6935C14.6074 11.6725 14.8366 11.7085 15.0468 11.7984L15.058 11.8036L19.1065 13.6177C19.3792 13.7353 19.6065 13.9379 19.7544 14.1954C19.9023 14.4528 19.963 14.7512 19.9272 15.046Z"
                fill="black"
              />
            </svg>
            {companyInfo?.phoneNumber}
          </a>
        )}

        <SocialMediaLinks companyInfo={companyInfo} />
      </div>
    </div>
  );
};

export default MenuInfoSection;
